import React, { useState, useRef } from 'react'
import { supabase } from '../lib/supabaseClient'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import Button from './ui/Button'
import heroPlaceholder from '../assets/hero-placeholder.svg'

const { FiUpload, FiImage, FiX } = FiIcons

const HeroImageUploader = ({ postId, currentImageUrl, onImageUpdate }) => {
  const [uploading, setUploading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState(currentImageUrl)
  const [imageError, setImageError] = useState(false)
  const fileInputRef = useRef(null)

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB')
      return
    }

    try {
      setUploading(true)
      setImageError(false)

      // Generate unique filename
      const timestamp = Date.now()
      const fileExt = file.name.split('.').pop()
      const fileName = `${timestamp}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`
      const filePath = `blog/${postId}/${fileName}`

      // Upload file to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('BLOG')
        .upload(filePath, file, {
          upsert: false
        })

      if (uploadError) throw uploadError

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('BLOG')
        .getPublicUrl(filePath)

      const publicUrl = urlData.publicUrl

      // Update preview immediately
      setPreviewUrl(publicUrl)

      // Call parent callback to update the form
      onImageUpdate(publicUrl)

    } catch (error) {
      console.error('Error uploading image:', error)
      alert('Failed to upload image. Please try again.')
    } finally {
      setUploading(false)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleRemoveImage = () => {
    setPreviewUrl(null)
    setImageError(false)
    onImageUpdate('')
  }

  const handleImageError = () => {
    setImageError(true)
  }

  // Determine which image to show
  const getImageSrc = () => {
    if (imageError || !previewUrl) {
      return heroPlaceholder
    }
    return previewUrl
  }

  const isPlaceholder = imageError || !previewUrl

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Hero Image
        </label>
        {previewUrl && !imageError && (
          <button
            type="button"
            onClick={handleRemoveImage}
            className="text-red-600 hover:text-red-700 text-sm"
          >
            Remove
          </button>
        )}
      </div>

      {/* Preview */}
      <div className="relative">
        <img
          src={getImageSrc()}
          alt={isPlaceholder ? "Hero image placeholder" : "Hero image preview"}
          className={`w-full h-48 object-cover rounded-lg border border-gray-300 ${
            isPlaceholder ? 'bg-gray-100' : ''
          }`}
          onError={handleImageError}
        />
        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-opacity rounded-lg flex items-center justify-center opacity-0 hover:opacity-100">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <SafeIcon icon={FiUpload} className="w-4 h-4 mr-2" />
            {isPlaceholder ? 'Upload' : 'Replace'}
          </Button>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Upload button when no image or placeholder */}
      {isPlaceholder && (
        <Button
          variant="secondary"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          loading={uploading}
          className="w-full"
        >
          <SafeIcon icon={FiUpload} className="w-4 h-4 mr-2" />
          {uploading ? 'Uploading...' : 'Upload Image'}
        </Button>
      )}

      {/* Image URL display (read-only) - only show for actual images */}
      {previewUrl && !imageError && (
        <div className="mt-2">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Image URL
          </label>
          <input
            type="text"
            value={previewUrl}
            readOnly
            className="w-full px-2 py-1 text-xs bg-gray-50 border border-gray-200 rounded text-gray-600 font-mono"
          />
        </div>
      )}
    </div>
  )
}

export default HeroImageUploader